// AddProfileForm.tsx
import React, { useState, useRef } from "react";
import { myMutation } from "@/hooks/myMutation";
import { useTransitDialog } from "@/context/TransitDialogContext";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { MyMultiStepFormBuilder } from "@/MY_COMPONENTS/MY_FORM/MyMultiStepFormBuilder";
import {
  UserIcon, HeartIcon, MapPinIcon, BriefcaseIcon,
  GraduationCapIcon, HeartPulseIcon, ImageIcon,
  ShieldCheckIcon, UsersIcon, BabyIcon, HomeIcon,
  SmileIcon, ActivityIcon, EyeIcon, MessageCircleIcon,
  Equal
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getIslandOptions } from "@/helpers/mvAtollIslandsHelper";
import getAge from "./profileHeplers/getAge";
import MySelect from "@/components/MySelect";
import MyInput from "@/MY_COMPONENTS/MY_INPUT/MyInput";
import SearchableSelectField from "@/MY_COMPONENTS/MY_FORM/fields/SearchableSelectField";
import IconButton from "@/components/actionButtons/IconButton";
import { FaFemale, FaMale } from "react-icons/fa";

// ============================================
// CONSTANTS & OPTIONS
// ============================================

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non-binary", label: "Non-binary" },
  { value: "prefer-not-to-say", label: "Prefer not to say" }
];

const SEEKING_STATUS_OPTIONS = [
  { value: "seeking", label: "Actively Seeking" },
  { value: "not_seeking", label: "Not Seeking" },
  { value: "inactive", label: "Inactive" },
  { value: "busy", label: "Busy" }
];

const READINESS_OPTIONS = [
  { value: "ready_now", label: "Ready Now" },
  { value: "in_a_few_months", label: "In a Few Months" },
  { value: "not_sure", label: "Not Sure" },
  { value: "just_exploring", label: "Just Exploring" }
];

const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" }
];

const WANT_KIDS_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe" }
];

const RELIGION_OPTIONS = [
  { value: "Muslim", label: "Muslim" },
  { value: "Christian", label: "Christian" },
  { value: "Jewish", label: "Jewish" },
  { value: "Hindu", label: "Hindu" },
  { value: "Buddhist", label: "Buddhist" },
  { value: "Other", label: "Other" },
  { value: "None", label: "None" }
];

const SECT_OPTIONS = [
  { value: "Sunni", label: "Sunni" },
  { value: "Shia", label: "Shia" },
  { value: "Other", label: "Other" }
];

const PRACTICE_LEVEL_OPTIONS = [
  { value: "Very practicing", label: "Very Practicing" },
  { value: "Moderate", label: "Moderate" },
  { value: "Less practicing", label: "Less Practicing" },
  { value: "Cultural only", label: "Cultural Only" }
];

const ISLAMIC_EDUCATION_OPTIONS = [
  { value: "none", label: "None" },
  { value: "basic", label: "Basic Quran Reading" },
  { value: "hifz", label: "Hifz (Memorization)" },
  { value: "alim", label: "Alim Course" },
  { value: "islamic_studies", label: "Islamic Studies Degree" }
];

const HAJJ_STATUS_OPTIONS = [
  { value: "not_performed", label: "Not Performed" },
  { value: "performed", label: "Performed Hajj" },
  { value: "performed_umrah", label: "Performed Umrah Only" },
  { value: "both", label: "Performed Both" }
];

const BODY_BUILD_OPTIONS = [
  { value: "Slim", label: "Slim" },
  { value: "Average", label: "Average" },
  { value: "Athletic", label: "Athletic" },
  { value: "Curvy", label: "Curvy" },
  { value: "Heavy", label: "Heavy" },
  { value: "Muscular", label: "Muscular" }
];

const COMPLEXION_OPTIONS = [
  { value: "Fair", label: "Fair" },
  { value: "Light", label: "Light" },
  { value: "Medium", label: "Medium" },
  { value: "Dark", label: "Dark" },
  { value: "Olive", label: "Olive" }
];

const HAIR_COLOR_OPTIONS = [
  { value: "Black", label: "Black" },
  { value: "Brown", label: "Brown" },
  { value: "Blonde", label: "Blonde" },
  { value: "Red", label: "Red" },
  { value: "Gray", label: "Gray" },
  { value: "Bald", label: "Bald" }
];

const EYE_COLOR_OPTIONS = [
  { value: "Brown", label: "Brown" },
  { value: "Blue", label: "Blue" },
  { value: "Green", label: "Green" },
  { value: "Hazel", label: "Hazel" },
  { value: "Gray", label: "Gray" }
];

const ETHNICITY_OPTIONS = [
  { value: "Asian", label: "Asian" },
  { value: "Black", label: "Black" },
  { value: "White", label: "White" },
  { value: "Hispanic", label: "Hispanic" },
  { value: "Mixed", label: "Mixed" },
  { value: "Middle Eastern", label: "Middle Eastern" },
  { value: "Other", label: "Other" }
];

const GENOTYPE_OPTIONS = [
  { value: "AA", label: "AA" },
  { value: "AS", label: "AS" },
  { value: "SS", label: "SS" },
  { value: "AC", label: "AC" },
  { value: "Unknown", label: "Unknown" }
];

const BLOOD_GROUP_OPTIONS = [
  { value: "A+", label: "A+" },
  { value: "A-", label: "A-" },
  { value: "B+", label: "B+" },
  { value: "B-", label: "B-" },
  { value: "O+", label: "O+" },
  { value: "O-", label: "O-" },
  { value: "AB+", label: "AB+" },
  { value: "AB-", label: "AB-" },
  { value: "Unknown", label: "Unknown" }
];

const EDUCATION_LEVEL_OPTIONS = [
  { value: "High School", label: "High School" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor's", label: "Bachelor's Degree" },
  { value: "Master's", label: "Master's Degree" },
  { value: "PhD", label: "PhD" },
  { value: "Professional", label: "Professional Degree" },
  { value: "Trade School", label: "Trade School" }
];

const HEALTH_STATUS_OPTIONS = [
  { value: "Excellent", label: "Excellent" },
  { value: "Good", label: "Good" },
  { value: "Fair", label: "Fair" },
  { value: "Poor", label: "Poor" }
];

const SMOKING_OPTIONS = [
  { value: "No", label: "No" },
  { value: "Occasionally", label: "Occasionally" },
  { value: "Regularly", label: "Regularly" },
  { value: "Trying to quit", label: "Trying to Quit" }
];

const ALCOHOL_OPTIONS = [
  { value: "No", label: "No" },
  { value: "Occasionally", label: "Occasionally" },
  { value: "Regularly", label: "Regularly" },
  { value: "Socially", label: "Socially" }
];

const RELOCATION_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Maybe" }
];

const POLYGAMY_OPTIONS = [
  { value: "yes", label: "Accept" },
  { value: "no", label: "Don't Accept" },
  { value: "maybe", label: "Maybe" }
];

// Popular interests for selection
const POPULAR_INTERESTS = [
  "Reading", "Sports", "Travel", "Cooking", "Photography",
  "Music", "Art", "Dancing", "Hiking", "Swimming",
  "Yoga", "Meditation", "Volunteering", "Gaming", "Movies",
  "Fashion", "Technology", "Business", "Teaching", "Writing"
];

const COUNTRY_OPTIONS = [
  { value: "MV", label: "Maldives" },
  { value: "IN", label: "India" },
  { value: "PK", label: "Pakistan" },
  { value: "SA", label: "Saudi Arabia" },
];




// ============================================
// FORM SCHEMA
// ============================================

export const createProfileSchema = {
  steps: [
    {
      id: "basic_info",
      icon: <UserIcon className="w-5 h-5" />,
      label: "Basic Information",
      fields: [
        {
          label: "UserId",
          topInfo: "Your Username is ",
          name: "user_id",
          type: "display_simple",
          renderCustom: (formState: any) => (
            <span>{formState?.user?.user_id || "-"}</span>
          )
        },
        {
          label: "Country",
          name: "country",
          required: true,
          type: "searchableselect",
          placeholder: "Your country",
          options: COUNTRY_OPTIONS
        },

        // {
        //   label: "City",
        //   name: "city",
        //   required: true,
        //   placeholder: "Your city",
        //   bottomInfo: "This is what public can see",
        //   type: "searchableselect",
        //   options: getIslandOptions("I"),
        //   visibleWhen: { field: "country", equals: "MV" },

        //   onSelect: (option, { setValue }: any) => {
        //     setValue("latitude", option?.lat || "");
        //     setValue("longitude", option?.lng || "");
        //   }
        // },
        {
          label: "City",
          name: "city",
          required: true,
          placeholder: "Your city",
          type: "custom",
          //wrapInBox: true, // Automatically wrapped in MyBox with label and error handling
          renderCustom: (context:any) => {
            const { getValue, setValue, formState, currentValue } = context;
            const country = getValue("country");
            const error = formState.errors.city;

            if (country === "MV") {
              return (
                <div>
                  <SearchableSelectField
                    field={{
                      label: "Island",
                      type: "searchableselect",
                      name: "city",
                      value: currentValue,
                      options: getIslandOptions("I"),
                      placeholder: "Select your island",
                      onChange: (value) => setValue("city", value, { shouldValidate: true })
                    }}
                  />
                  {error && (
                    <p className="text-xs text-red-600 mt-1">{error.message}</p>
                  )}
                </div>
              );
            }

            return (
              <MyInput
                field={{
                  label: "City",
                  type: "text",
                  name: "city",
                  required: true,
                  placeholder: "Enter your city",
                  value: currentValue,
                  onChange: (value) => {
                    setValue("city", value, { shouldValidate: true, shouldDirty: true });
                  },
                  // onBlur: () => trigger("city"),
                  // error: error?.message as string,
                  // helperText: "This is what public can see",
                }}
              />
            );
          }
        },

        {
          label: "Nickname / Display Name",
          name: "nickname",
          required: true,
          type: "text",
          placeholder: "How would you like to be called?",
          helperText: "This is what others will see"
        },
        {
          label: "DOB",
          name: "dob",
          required: true,
          type: "date_native",
          placeholder: "Your date of birth"
        },
        {
          label: "Age",
          name: "age",
          //required: true,
          type: "display",
          visibleWhen: { field: "dob", notEmpty: true },
          placeholder: "Your age",
          renderCustom: (formState: any) => {
            const dob = formState.dob;
            if (!dob) return <span className="text-gray-400">Enter your DOB to calculate age</span>;
            return <span>{getAge(dob)} years</span>;
          }
        },
        {
          label: "Gender",
          name: "gender",
          required: true,
          type: "selectbuttons",
          options: GENDER_OPTIONS
        },
        {
          label: "Seeking Status",
          name: "seeking_status",
          type: "selectbuttons",
          options: SEEKING_STATUS_OPTIONS,
          defaultValue: "seeking"
        },
        {
          label: "Readiness",
          name: "readiness",
          type: "selectbuttons",
          options: READINESS_OPTIONS,
          defaultValue: "ready_now"
        },
        {
          label: "Marital Status",
          name: "marital_status",
          type: "selectbuttons",
          options: MARITAL_STATUS_OPTIONS,
          defaultValue: "single"
        },
        {
          label: "Number of Children",
          name: "kids_count",
          type: "number",
          min: 0,
          max: 20,
          defaultValue: 0,
          placeholder: "How many children do you have?"
        },
        {
          label: "Want Children?",
          name: "want_kids",
          type: "selectbuttons",
          options: WANT_KIDS_OPTIONS,
          defaultValue: "yes"
        }
      ]
    },

    {
      id: "location",
      icon: <MapPinIcon className="w-5 h-5" />,
      label: "Location",
      fields: [

        // {
        //   label: "Origin State/Region",
        //   name: "origin_state",
        //   type: "text",
        //   placeholder: "Where are you originally from?"
        // },

        {
          label: "Short Bio / About Me",
          name: "bio",
          type: "textarea",
          placeholder: "Tell others about yourself...",
          helperText: "Share your personality, values, and what makes you unique"
        },
        {
          label: "Looking For Summary",
          name: "looking_for_summary",
          type: "text",
          placeholder: "Briefly describe what you're looking for",
          helperText: "A short headline about your ideal match"
        }
      ]
    },

    {
      id: "religion",
      icon: <ShieldCheckIcon className="w-5 h-5" />,
      label: "Religion & Values",
      fields: [
        {
          label: "Religion",
          name: "religion",
          type: "selectbuttons",
          options: RELIGION_OPTIONS,
          defaultValue: "Muslim"
        },
        {
          label: "Sect",
          name: "sect",
          type: "selectbuttons",
          options: SECT_OPTIONS,
          defaultValue: "Sunni"
        },
        {
          label: "Practice Level",
          name: "practice_level",
          type: "selectbuttons",
          options: PRACTICE_LEVEL_OPTIONS,
          helperText: "How religious/practicing are you?"
        },
        {
          label: "Islamic Education",
          name: "islamic_education",
          type: "selectbuttons",
          options: ISLAMIC_EDUCATION_OPTIONS
        },
        {
          label: "Hajj/Umrah Status",
          name: "hajj_status",
          type: "selectbuttons",
          options: HAJJ_STATUS_OPTIONS
        }
      ]
    },

    {
      id: "appearance",
      icon: <SmileIcon className="w-5 h-5" />,
      label: "Appearance",
      fields: [
        {
          label: "Height (cm)",
          name: "height_cm",
          type: "number",
          showSlider: true,
          min: 100,
          max: 250,
          placeholder: "Your height in cm"
        },
        {
          label: "Weight (kg)",
          name: "weight_kg",
          type: "number",
          showSlider: true,
          min: 30,
          max: 300,
          placeholder: "Your weight in kg (optional)"
        },
        {
          label: "Body Build",
          name: "body_build",
          type: "selectbuttons",
          options: BODY_BUILD_OPTIONS
        },
        // {
        //   label: "Complexion",
        //   name: "complexion",
        //   type: "selectbuttons",
        //   options: COMPLEXION_OPTIONS
        // },
        {
          label: "Hair Color",
          name: "hair_color",
          type: "selectbuttons",
          options: HAIR_COLOR_OPTIONS
        },
        {
          label: "Eye Color",
          name: "eye_color",
          type: "selectbuttons",
          options: EYE_COLOR_OPTIONS
        },
        {
          label: "Ethnicity",
          name: "ethnicity",
          type: "selectbuttons",
          options: ETHNICITY_OPTIONS
        },
        {
          label: "Tribe",
          name: "tribe",
          type: "text",
          placeholder: "Your tribe/ethnic group"
        }
      ]
    },

    // {
    //   id: "health_lifestyle",
    //   icon: <HeartPulseIcon className="w-5 h-5" />,
    //   label: "Health & Lifestyle",
    //   fields: [
    //     // {
    //     //   label: "Genotype",
    //     //   name: "genotype",
    //     //   type: "selectbuttons",
    //     //   options: GENOTYPE_OPTIONS
    //     // },
    //     {
    //       label: "Blood Group",
    //       name: "blood_group",
    //       type: "selectbuttons",
    //       options: BLOOD_GROUP_OPTIONS
    //     },
    //     {
    //       label: "Health Status",
    //       name: "health_status",
    //       type: "selectbuttons",
    //       options: HEALTH_STATUS_OPTIONS,
    //       defaultValue: "Excellent"
    //     },
    //     {
    //       label: "Smoking",
    //       name: "smoking",
    //       type: "selectbuttons",
    //       options: SMOKING_OPTIONS,
    //       defaultValue: "No"
    //     },
    //     // {
    //     //   label: "Alcohol",
    //     //   name: "alcohol",
    //     //   type: "selectbuttons",
    //     //   options: ALCOHOL_OPTIONS,
    //     //   defaultValue: "No"
    //     // }
    //   ]
    // },

    {
      id: "education_career",
      icon: <GraduationCapIcon className="w-5 h-5" />,
      label: "Education & Career",
      fields: [
        {
          label: "Education Level",
          name: "education_level",
          type: "selectbuttons",
          options: EDUCATION_LEVEL_OPTIONS
        },
        {
          label: "Occupation",
          name: "occupation",
          type: "text",
          placeholder: "Your profession"
        },
        {
          label: "Interests",
          name: "interests",
          type: "selectbuttons",
          multiple: true,
          options: POPULAR_INTERESTS.map(i => ({ value: i, label: i })),
          helperText: "Select your hobbies and interests"
        }
      ]
    },

    {
      id: "preferences",
      icon: <HeartIcon className="w-5 h-5" />,
      label: "I am Looking For",
      fields: [
        {
          label: "Preferred Age Range",
          name: "preferences",
          type: "display",
          renderCustom: (formState: any) => (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Min Age</label>
                <input
                  type="number"
                  name="preferred_age_min"
                  className="w-full rounded-lg border p-2"
                  placeholder="18"
                  defaultValue={18}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Age</label>
                <input
                  type="number"
                  name="preferred_age_max"
                  className="w-full rounded-lg border p-2"
                  placeholder="99"
                  defaultValue={formState.age ? parseInt(formState.age) + 10 : 99}
                />
              </div>
            </div>
          )
        },
        {
          label: "Looking For Gender",
          name: "lf_gender",
          type: "display_simple",
          topInfo: "Im looking for",
          options: GENDER_OPTIONS,
          // helperText: "Who are you interested in?",
          renderCustom: (formState: any) => {
             return (<IconButton icon={formState.gender === "male"? <FaFemale/>: <FaMale/>} label={formState.gender === "male"? "Female": "Male"} />)}
        },
        {
          label: "I prefer Who Accepts Relocation?",
          name: "lf_relocation",
          type: "selectbuttons",
          defaultValue: "Yes",
          options: RELOCATION_OPTIONS
        },
        {
          label: "I prefer Who Accepts Polygamy?",
          name: "lf_polygamy",
          type: "selectbuttons",
          defaultValue: "No",
          options: POLYGAMY_OPTIONS
        },
        {
          label: "I prefer Someone with Children?",
          name: "lf_accepts_children",
          type: "selectbuttons",
          defaultValue: "No",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
            { value: "maybe", label: "Maybe" }
          ]
        },
        {
          label: "Desired Qualities",
          name: "desired_qualities",
          type: "selectbuttons",
          multiple: true,
          placeholder: "What qualities are you looking for in a partner?",
          helperText: "e.g., Practicing Muslim, kind, family-oriented, financially stable",
          options: [
            { value: "lovely", label: "Lovely" },
            { value: "practicing Muslim", label: "Practicing Muslim" },
            { value: "family-oriented", label: "Family-oriented" },
            { value: "financially stable", label: "Financially stable" }
          ]
        }
      ],
      // onEnterApi: {
      //   endpoint: MY_ENDPOINTS.PROFILES_ENDPOINTS.CREATE_PROFILE.path,
      //   method: "POST",
      //   saveResponseTo: "user_id",  // Save the returned user_id to form state
      // },
    },

    {
      id: "photos",
      icon: <ImageIcon className="w-5 h-5" />,
      label: "Photos",
      fields: [
        {
          label: "Profile Photos",
          name: "photos",
          type: "uploader",
          multiple: true,
          renameTemplate: "profile_{user_id}_{timestamp}.jpg",
          accept: "image/*",
          helperText: "Upload your best photos. First photo will be your main profile picture.",
          //required: true
        }
      ]
    }
  ]
};

// ============================================
// MAIN COMPONENT
// ============================================

export default function AddProfileForm({
  onSuccess,
  onClose
}: {
  onSuccess?: (profile: any) => void;
  onClose?: () => void;
}) {
  const { user } = useAuth();
  const formMethodsRef = useRef<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const transitDialog = useTransitDialog();

  // Create profile mutation
  const createProfile = myMutation({
    endpoint: MY_ENDPOINTS.PROFILES_ENDPOINTS.CREATE_PROFILE.path,
    method: "POST",
    onSuccess: (res: any) => {
      transitDialog.showSuccess("Profile created successfully! 🎉");
      if (onSuccess) onSuccess(res.data);
      if (onClose) onClose();
    },
    onError: (error: any) => {
      transitDialog.showError("Failed to create profile: " + (error.message || "Unknown error"));
      console.error("Create profile error:", error);
    }
  });

  function handleFinalSubmit(data: any) {
    console.log("Final submit data:", data);

    // Validate required fields
    if (!data.nickname) {
      transitDialog.showError("Nickname is required");
      return;
    }

    const userAge = getAge(data.dob);

    if (!userAge || userAge < 18) {
      transitDialog.showError("Valid age (18+) is required");
      return;
    }

    if (!data.gender) {
      transitDialog.showError("Gender is required");
      return;
    }

    // Build preferences JSON
    const preferences = {
      preferred_age_min: data.preferred_age_min ? parseInt(data.preferred_age_min) : 18,
      preferred_age_max: data.preferred_age_max ? parseInt(data.preferred_age_max) : 99,
      relocation: data.lf_relocation || "maybe",
      polygamy: data.lf_polygamy || "maybe",
      accepts_children: data.lf_accepts_children || "maybe",
      preferred_gender: data.lf_gender || "female"
    };

    // Build desired qualities
    // const desired_qualities = data.desired_qualities ? {
    //   qualities: data.desired_qualities.split('\n').filter((q: string) => q.trim())
    // } : {};

    // Build interests array
    const interests = Array.isArray(data.interests) ? data.interests : [];

    // Handle photos
    let photos = [];
    if (data.photos) {
      if (Array.isArray(data.photos)) {
        photos = data.photos.map((photo: any) => {
          if (typeof photo === 'string') return photo;
          if (photo instanceof File) return URL.createObjectURL(photo);
          return photo;
        });
      } else if (typeof data.photos === 'string') {
        photos = [data.photos];
      }
    }

    // Prepare the payload
    const payload = {
      nickname: String(data.nickname),
      age: parseInt(data.age),
      gender: data.gender,
      city: data.city?.value || null,
      country: data.country || null,
      origin_state: data.origin_state || null,
      bio: data.bio || null,
      interests: interests,
      height_cm: data.height_cm ? parseInt(data.height_cm) : null,
      body_build: data.body_build || null,
      complexion: data.complexion || null,
      weight_kg: data.weight_kg ? parseInt(data.weight_kg) : null,
      hair_color: data.hair_color || null,
      eye_color: data.eye_color || null,
      ethnicity: data.ethnicity || null,
      tribe: data.tribe || null,
      seeking_status: data.seeking_status || "seeking",
      readiness: data.readiness || "ready_now",
      preferences: preferences,
      //desired_qualities: desired_qualities,
      genotype: data.genotype || null,
      blood_group: data.blood_group || null,
      education_level: data.education_level || null,
      occupation: data.occupation || null,
      photos: photos,
      private_data: {
        full_photos: photos,
        // Add private contact info here if needed
      },
      religion: data.religion || "Muslim",
      sect: data.sect || "Sunni",
      practice_level: data.practice_level || null,
      islamic_education: data.islamic_education || null,
      hajj_status: data.hajj_status || null,
      health_status: data.health_status || "Excellent",
      smoking: data.smoking || "No",
      alcohol: data.alcohol || "No",
      looking_for_summary: data.looking_for_summary || null
    };

    console.log("Submitting payload:", payload);
    createProfile.mutate(payload);
  }



  return (
    <div className="w-full relative dark:bg-gray-900 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-2 flex flex-col">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <UserIcon className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Create Your Profile
            </h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Tell us about yourself to find meaningful connections
        </p>
      </div>

      {/* Form Content */}
      <div className="w-full max-h-[calc(100vh-200px)] overflow-y-auto p-0 ">
        <MyMultiStepFormBuilder
          schema={createProfileSchema}
          onFinalSubmit={handleFinalSubmit}
          onDataChange={(data) => {
            console.log("Form data changed:", data);
            setFormData(data);
          }}
          onFormReady={(methods) => {
            console.log("Form ready, setting up methods");
            formMethodsRef.current = methods;
            // Set default values
            methods.setValue("user", user);
            methods.setValue("gender", "male");
            methods.setValue("seeking_status", "seeking");
            methods.setValue("readiness", "ready_now");
            methods.setValue("marital_status", "single");
            methods.setValue("kids_count", 0);
            methods.setValue("religion", "Muslim");
            methods.setValue("sect", "Sunni");
            methods.setValue("health_status", "Excellent");
            methods.setValue("smoking", "No");
            methods.setValue("alcohol", "No");
            methods.setValue("preferred_age_min", 18);
            methods.setValue("preferred_age_max", 99);
          }}
        />
      </div>

      {/* Debug View - Remove in production */}
      {import.meta.env.NODE_ENV === "development" && (
        <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mx-6 mb-6">
          <div className="text-sm font-mono">
            <details>
              <summary className="cursor-pointer font-semibold mb-2 text-gray-700 dark:text-gray-300">
                Form Data (Debug) - {Object.keys(formData).length} fields
              </summary>
              <pre className="text-xs overflow-auto max-h-96 text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-900 rounded">
                {JSON.stringify(formData, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}